import { useState, useEffect, useRef, type RefObject } from 'react';
import { useIsomorphicLayoutEffect } from 'dibs-react-hooks/exports/useIsomorphicLayoutEffect.ts';
import { type OnVisibilityChange } from './types.ts';

type ObserverInstance = {
    observer: IntersectionObserver;
    callbackMap: WeakMap<Element, (entry: IntersectionObserverEntry) => void>;
};

const observersMap = new Map<string, ObserverInstance>();

export function getObserver(optionsKey: string, isEnabled: true): ObserverInstance;
export function getObserver(optionsKey: string, isEnabled: boolean): ObserverInstance | null;
export function getObserver(optionsKey: string, isEnabled: boolean): ObserverInstance | null {
    const observerInstance = observersMap.get(optionsKey);
    if (observerInstance) {
        return observerInstance;
    } else if (isEnabled) {
        // Parsing is happening only once, to create new IntersectionObserver instance
        const options = JSON.parse(optionsKey);
        const observer = new IntersectionObserver(entries => {
            entries.forEach(entry => {
                const callback = observersMap.get(optionsKey)?.callbackMap.get(entry.target);
                callback?.(entry);
            });
        }, options);

        const newInstance = {
            observer,
            callbackMap: new WeakMap<Element, (entry: IntersectionObserverEntry) => void>(),
        };
        observersMap.set(optionsKey, newInstance);
        return newInstance;
    }
    // Do not create instance for disabled observer
    return null;
}

/*
 * @description A hook that tracks the visibility of a ref element. Uses singleton pattern, i.e. creates a single instance of the Intersection Observer for each options.
 * @param options - The options for the Intersection Observer. Value need to be consistent between renders.
 * @param elementRef - The ref object to track the visibility of.
 * @param onVisibilityChange - The callback function to call when the visibility changes.
 * @param isEnabled - Since hooks cannot be conditionally called, this is used to determine if the intersection observer should be created and observing the target element.
 * @param watchAfterFirstVisible - Whether to watch after the first visible change.
 */
export const useVisibilityTracker = ({
    elementRef,
    onVisibilityChange,
    options,
    isEnabled = true,
    watchAfterFirstVisible = false,
}: {
    elementRef: RefObject<Element | null>;
    onVisibilityChange: OnVisibilityChange;
    options?: IntersectionObserverInit;
    isEnabled?: boolean;
    watchAfterFirstVisible?: boolean;
}): void => {
    const callbackRef = useRef(onVisibilityChange);

    // Stringification is required to create only one observer instance per options. This is going to be executed only once, during the first render.
    const [optionsKey] = useState(() => (options ? JSON.stringify(options) : '{}'));

    // Keep the callback ref up to date
    // Can we refactor this by wrapping callback with useEffectEvent?
    useIsomorphicLayoutEffect(() => {
        callbackRef.current = onVisibilityChange;
    });

    useEffect(() => {
        const target = elementRef.current;
        // If no target - do nothing
        if (target) {
            // Start observing the target, create instance if needed
            if (isEnabled) {
                const { observer, callbackMap } = getObserver(optionsKey, isEnabled);

                // Create a stable proxy function
                const handler = ({
                    intersectionRatio,
                    boundingClientRect,
                    isIntersecting,
                }: IntersectionObserverEntry): void => {
                    callbackRef.current?.({
                        intersectionRatio,
                        boundingClientRect,
                        isVisible: isIntersecting,
                        disconnect: () => {
                            observer.unobserve(target);
                            callbackMap.delete(target);
                        },
                    });

                    if (isIntersecting && !watchAfterFirstVisible) {
                        observer.unobserve(target);
                        callbackMap.delete(target);
                    }
                };

                callbackMap.set(target, handler);
                observer.observe(target);
            } else {
                // Stop observing the target
                const instance = getObserver(optionsKey, isEnabled);
                if (instance) {
                    instance.observer.unobserve(target);
                    instance.callbackMap.delete(target);
                }
            }
        }

        return () => {
            if (target) {
                const instance = getObserver(optionsKey, isEnabled);
                if (instance) {
                    instance.observer.unobserve(target);
                    instance.callbackMap.delete(target);
                }
            }
        };
    }, [elementRef, optionsKey, watchAfterFirstVisible, isEnabled]);
};
